import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { getAppVersion } from '../../graphql/queries';
import { updateAppVersion, deleteAppVersion } from '../../graphql/mutation';
import { 
  Card, 
  Form, 
  Input, 
  Button, 
  Space, 
  Row, 
  Col,
  Descriptions,
  Modal,
  Switch,
  Tag
} from 'antd';
import { SaveOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { showSnackBar } from '../../components/SnackBar/SnackBar';
import './AppVersionDetail.css';

const AppVersionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const { data: appVersionData, loading: fetchLoading } = useQuery(getAppVersion, {
    variables: { appVersionId: id },
  });

  const [updateAppVersionMutation] = useMutation(updateAppVersion);
  const [deleteAppVersionMutation] = useMutation(deleteAppVersion);

  useEffect(() => {
    if (appVersionData?.getAppVersion) {
      const appVersion = appVersionData.getAppVersion;
      form.setFieldsValue({
        version: appVersion.version,
        reviewMode: appVersion.reviewMode ?? false,
      });
    }
  }, [appVersionData, form]);

  const handleUpdate = async (values) => {
    try {
      setLoading(true);

      await updateAppVersionMutation({
        variables: {
          appVersionId: id,
          version: values.version,
          reviewMode: values.reviewMode,
        }
      });

      showSnackBar('success', 'Success', 'App version updated successfully');
      setIsEditing(false);
      window.location.reload();
    } catch (error) {
      showSnackBar('error', 'Error', error.message || 'Failed to update app version');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = () => {
    Modal.confirm({
      title: 'Delete App Version',
      content: 'Are you sure you want to delete this app version? This action cannot be undone.',
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await deleteAppVersionMutation({
            variables: { appVersionId: id }
          });
          showSnackBar('success', 'Success', 'App version deleted successfully');
          navigate('/app-versions');
        } catch (error) {
          showSnackBar('error', 'Error', 'Failed to delete app version');
        }
      }
    });
  };

  const appVersion = appVersionData?.getAppVersion;

  if (fetchLoading) {
    return (
      <div className="app-version-detail-container">
        <Card loading />
      </div>
    );
  }

  if (!appVersion) {
    return (
      <div className="app-version-detail-container">
        <Card title="App Version Not Found">
          <p>The requested app version could not be found.</p>
          <Button onClick={() => navigate('/app-versions')}>
            Back to App Versions
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="app-version-detail-container">
      <Card 
        title={`App Version: ${appVersion.version}`}
        extra={
          <Space>
            {!isEditing && (
              <Button 
                type="primary" 
                icon={<EditOutlined />}
                onClick={() => setIsEditing(true)}
              >
                Edit
              </Button>
            )}
            <Button 
              danger 
              icon={<DeleteOutlined />}
              onClick={handleDelete}
            >
              Delete
            </Button>
          </Space>
        }
      >
        {!isEditing ? (
          <div className="app-version-details-view">
            <Descriptions column={1} bordered>
              <Descriptions.Item label="ID">{appVersion.id}</Descriptions.Item>
              <Descriptions.Item label="Version">{appVersion.version}</Descriptions.Item>
              <Descriptions.Item label="Review Mode">
                <Tag color={appVersion.reviewMode ? 'orange' : 'green'}>
                  {appVersion.reviewMode ? 'Review Mode Enabled' : 'Normal Mode'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Created At">
                {new Date(appVersion.createdAt).toLocaleString()}
              </Descriptions.Item>
              <Descriptions.Item label="Updated At">
                {new Date(appVersion.updatedAt).toLocaleString()}
              </Descriptions.Item>
            </Descriptions>
          </div>
        ) : (
          <Form
            form={form}
            layout="vertical"
            onFinish={handleUpdate}
          >
            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  name="version"
                  label="Version"
                  rules={[{ required: true, message: 'Please enter version' }]}
                >
                  <Input placeholder="Enter version (e.g., 1.0.0)" />
                </Form.Item>

                <Form.Item
                  name="reviewMode"
                  label="Review Mode"
                  valuePropName="checked"
                >
                  <Switch 
                    checkedChildren="Review" 
                    unCheckedChildren="Normal"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item>
              <Space>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  loading={loading}
                  icon={<SaveOutlined />}
                >
                  Update App Version
                </Button>
                <Button onClick={() => setIsEditing(false)}>
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Form>
        )}
      </Card>
    </div>
  );
};

export default AppVersionDetail;
